"use client";

import { useState } from "react";
import { motion } from "motion/react";
import { Mail, ArrowRight, Check, Gift, Sparkles, Lock, Globe2 } from "lucide-react";
import { useLang } from "@/context/lang";

type Copy = {
  eyebrow: string;
  title1: string;
  title2: string;
  sub: string;
  placeholder: string;
  cta: string;
  consent: string;
  success: string;
  bullets: { icon: "gift" | "sparkles" | "lock" | "globe"; text: string }[];
  stat1: string;
  stat1Label: string;
  stat2: string;
  stat2Label: string;
};

const COPY: Record<string, Copy> = {
  tr: {
    eyebrow: "BÜLTEN",
    title1: "Yapay zekayla ses üretiminin",
    title2: "geleceği e-postanızda.",
    sub: "Yeni sesler, kişisel indirim kodları ve sektör trendleri. Haftada bir, kısa ve özlü.",
    placeholder: "ornek@email.com",
    cta: "Abone ol",
    consent: "Tek tıkla iptal. Spam yok. E-postanız üçüncü taraflarla paylaşılmaz.",
    success: "Hoş geldiniz! İlk bülten birkaç gün içinde.",
    bullets: [
      { icon: "gift", text: "Yeni abonelere özel %15 indirim kodu" },
      { icon: "sparkles", text: "Önce siz: yeni ses ve özellikler" },
      { icon: "globe", text: "Sektör trendleri ve gerçek kullanım örnekleri" },
      { icon: "lock", text: "İstediğinizde tek tıkla aboneliği iptal" },
    ],
    stat1: "Haftalık",
    stat1Label: "Yayın sıklığı",
    stat2: "Spam yok",
    stat2Label: "Söz veriyoruz",
  },
  en: {
    eyebrow: "NEWSLETTER",
    title1: "The future of AI voice",
    title2: "delivered to your inbox.",
    sub: "New voices, personal discount codes and industry trends. Once a week. Short and useful.",
    placeholder: "you@email.com",
    cta: "Subscribe",
    consent: "One-click unsubscribe. No spam. We never share your email.",
    success: "Welcome aboard. First issue arrives in a few days.",
    bullets: [
      { icon: "gift", text: "Exclusive 15% off code for new subscribers" },
      { icon: "sparkles", text: "Early access to new voices and features" },
      { icon: "globe", text: "Industry trends and real use cases" },
      { icon: "lock", text: "One-click unsubscribe anytime" },
    ],
    stat1: "Weekly",
    stat1Label: "Cadence",
    stat2: "No spam",
    stat2Label: "We promise",
  },
  ar: {
    eyebrow: "النشرة",
    title1: "مستقبل الصوت بالذكاء الاصطناعي",
    title2: "في بريدك الإلكتروني.",
    sub: "أصوات جديدة، أكواد خصم شخصية وأحدث اتجاهات الصناعة. مرة واحدة أسبوعياً.",
    placeholder: "you@email.com",
    cta: "اشترك",
    consent: "إلغاء بنقرة واحدة. لا رسائل مزعجة.",
    success: "أهلاً بك! النشرة الأولى خلال أيام.",
    bullets: [
      { icon: "gift", text: "خصم 15% للمشتركين الجدد" },
      { icon: "sparkles", text: "وصول مبكر للأصوات والميزات الجديدة" },
      { icon: "globe", text: "اتجاهات الصناعة وأمثلة استخدام حقيقية" },
      { icon: "lock", text: "إلغاء الاشتراك بنقرة واحدة" },
    ],
    stat1: "أسبوعياً",
    stat1Label: "التكرار",
    stat2: "بدون سبام",
    stat2Label: "نعدك",
  },
  es: {
    eyebrow: "NEWSLETTER",
    title1: "El futuro de la voz IA",
    title2: "directo a tu bandeja.",
    sub: "Nuevas voces, códigos de descuento personales y tendencias. Una vez por semana.",
    placeholder: "tu@email.com",
    cta: "Suscribirme",
    consent: "Cancelación con un clic. Sin spam.",
    success: "¡Bienvenido! Primera edición en pocos días.",
    bullets: [
      { icon: "gift", text: "15% de descuento exclusivo al suscribirte" },
      { icon: "sparkles", text: "Acceso anticipado a nuevas voces" },
      { icon: "globe", text: "Tendencias y casos reales de uso" },
      { icon: "lock", text: "Cancelación con un clic" },
    ],
    stat1: "Semanal",
    stat1Label: "Frecuencia",
    stat2: "Sin spam",
    stat2Label: "Lo prometemos",
  },
  de: {
    eyebrow: "NEWSLETTER",
    title1: "Die Zukunft der KI-Stimme",
    title2: "direkt in deinem Postfach.",
    sub: "Neue Stimmen, persönliche Rabattcodes und Branchentrends. Einmal pro Woche.",
    placeholder: "du@email.com",
    cta: "Abonnieren",
    consent: "Abmeldung mit einem Klick. Kein Spam.",
    success: "Willkommen! Erste Ausgabe in wenigen Tagen.",
    bullets: [
      { icon: "gift", text: "15% Rabattcode exklusiv für neue Abonnenten" },
      { icon: "sparkles", text: "Früher Zugang zu neuen Stimmen" },
      { icon: "globe", text: "Branchentrends und reale Anwendungsfälle" },
      { icon: "lock", text: "Abmeldung mit einem Klick" },
    ],
    stat1: "Wöchentlich",
    stat1Label: "Häufigkeit",
    stat2: "Kein Spam",
    stat2Label: "Versprochen",
  },
  it: {
    eyebrow: "NEWSLETTER",
    title1: "Il futuro della voce AI",
    title2: "nella tua casella.",
    sub: "Nuove voci, codici sconto personali e tendenze del settore. Una volta a settimana.",
    placeholder: "tu@email.com",
    cta: "Iscriviti",
    consent: "Annullamento con un clic. Niente spam.",
    success: "Benvenuto! Prima edizione tra pochi giorni.",
    bullets: [
      { icon: "gift", text: "Sconto del 15% per nuovi iscritti" },
      { icon: "sparkles", text: "Accesso anticipato a nuove voci" },
      { icon: "globe", text: "Tendenze e casi d'uso reali" },
      { icon: "lock", text: "Annullamento con un clic" },
    ],
    stat1: "Settimanale",
    stat1Label: "Frequenza",
    stat2: "Niente spam",
    stat2Label: "Lo promettiamo",
  },
  ru: {
    eyebrow: "РАССЫЛКА",
    title1: "Будущее ИИ-голоса",
    title2: "прямо в вашей почте.",
    sub: "Новые голоса, персональные коды скидок и тренды. Раз в неделю.",
    placeholder: "you@email.com",
    cta: "Подписаться",
    consent: "Отписка в один клик. Без спама.",
    success: "Добро пожаловать! Первый выпуск скоро.",
    bullets: [
      { icon: "gift", text: "Эксклюзивная скидка 15% при подписке" },
      { icon: "sparkles", text: "Ранний доступ к новым голосам" },
      { icon: "globe", text: "Тренды и реальные кейсы" },
      { icon: "lock", text: "Отписка в один клик" },
    ],
    stat1: "Еженедельно",
    stat1Label: "Частота",
    stat2: "Без спама",
    stat2Label: "Обещаем",
  },
  fr: {
    eyebrow: "NEWSLETTER",
    title1: "L'avenir de la voix IA",
    title2: "directement dans votre boîte.",
    sub: "Nouvelles voix, codes promo personnels et tendances. Une fois par semaine.",
    placeholder: "vous@email.com",
    cta: "S'abonner",
    consent: "Désabonnement en un clic. Pas de spam.",
    success: "Bienvenue ! Premier numéro dans quelques jours.",
    bullets: [
      { icon: "gift", text: "Code promo de 15% pour les nouveaux abonnés" },
      { icon: "sparkles", text: "Accès anticipé aux nouvelles voix" },
      { icon: "globe", text: "Tendances et cas d'usage réels" },
      { icon: "lock", text: "Désabonnement en un clic" },
    ],
    stat1: "Hebdomadaire",
    stat1Label: "Fréquence",
    stat2: "Pas de spam",
    stat2Label: "Promis",
  },
  pt: {
    eyebrow: "NEWSLETTER",
    title1: "O futuro da voz IA",
    title2: "direto na sua caixa.",
    sub: "Novas vozes, códigos de desconto pessoais e tendências. Uma vez por semana.",
    placeholder: "voce@email.com",
    cta: "Assinar",
    consent: "Cancelamento com um clique. Sem spam.",
    success: "Bem-vindo! Primeira edição em alguns dias.",
    bullets: [
      { icon: "gift", text: "Cupom exclusivo de 15% para novos assinantes" },
      { icon: "sparkles", text: "Acesso antecipado a novas vozes" },
      { icon: "globe", text: "Tendências e casos de uso reais" },
      { icon: "lock", text: "Cancelamento com um clique" },
    ],
    stat1: "Semanal",
    stat1Label: "Frequência",
    stat2: "Sem spam",
    stat2Label: "Prometemos",
  },
  bs: {
    eyebrow: "NEWSLETTER",
    title1: "Budućnost AI glasa",
    title2: "stiže u vaš inbox.",
    sub: "Novi glasovi, lični kodovi za popust i trendovi industrije. Jednom sedmično.",
    placeholder: "vi@email.com",
    cta: "Pretplatite se",
    consent: "Otkaz jednim klikom. Bez spama.",
    success: "Dobrodošli! Prvi broj stiže za par dana.",
    bullets: [
      { icon: "gift", text: "Ekskluzivni 15% popust za nove pretplatnike" },
      { icon: "sparkles", text: "Raniji pristup novim glasovima" },
      { icon: "globe", text: "Trendovi i stvarne primjene" },
      { icon: "lock", text: "Otkaz jednim klikom" },
    ],
    stat1: "Sedmično",
    stat1Label: "Frekvencija",
    stat2: "Bez spama",
    stat2Label: "Obećavamo",
  },
};

const ICONS = { gift: Gift, sparkles: Sparkles, lock: Lock, globe: Globe2 };

export function Newsletter() {
  const { lang } = useLang();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "ok" | "err">("idle");
  const [error, setError] = useState<string | null>(null);
  const t = COPY[lang as keyof typeof COPY] || COPY.en;
  const isRtl = lang === "ar";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim().toLowerCase();
    if (!trimmed.includes("@")) {
      setError("Email?");
      return;
    }
    setStatus("loading");
    try {
      const r = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, lang, source: "homepage" }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) {
        setStatus("err");
        setError(d.error || `HTTP ${r.status}`);
        return;
      }
      setStatus("ok");
      setEmail("");
    } catch {
      setStatus("err");
      setError("Network error");
    }
  };

  return (
    <section className="relative py-24 sm:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Backdrop — deep gradient base, brand-aligned */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-950" />

      {/* Soft animated brand orbs */}
      <motion.div
        aria-hidden
        className="absolute top-1/4 -left-32 w-[480px] h-[480px] rounded-full bg-cyan-500/25 blur-[120px]"
        animate={{ x: [0, 60, 0], y: [0, -30, 0], scale: [1, 1.1, 1] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="absolute bottom-0 -right-32 w-[520px] h-[520px] rounded-full bg-teal-500/20 blur-[140px]"
        animate={{ x: [0, -50, 0], y: [0, 40, 0], scale: [1, 0.9, 1] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* Subtle dot grid */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />

      <div className="relative max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">
          {/* ── Left: bold headline + bullets ── */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            dir={isRtl ? "rtl" : "ltr"}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/15 border border-cyan-400/30 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
              <span className="text-[10px] font-black tracking-[0.2em] text-cyan-300">
                {t.eyebrow}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-white">
              {t.title1}
              <br />
              <span className="bg-gradient-to-r from-cyan-300 via-cyan-200 to-teal-200 bg-clip-text text-transparent">
                {t.title2}
              </span>
            </h2>

            <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl">
              {t.sub}
            </p>

            {/* Bullets grid */}
            <ul className="mt-10 grid sm:grid-cols-2 gap-4 max-w-xl">
              {t.bullets.map((b, i) => {
                const Icon = ICONS[b.icon];
                return (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 * i, duration: 0.4 }}
                    className="flex items-start gap-3"
                  >
                    <div className="shrink-0 w-9 h-9 rounded-xl bg-cyan-500/15 border border-cyan-400/20 flex items-center justify-center">
                      <Icon size={16} className="text-cyan-300" strokeWidth={2.2} />
                    </div>
                    <span className="text-sm text-slate-200 leading-snug pt-1">
                      {b.text}
                    </span>
                  </motion.li>
                );
              })}
            </ul>
          </motion.div>

          {/* ── Right: form card with floating stats ── */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative"
            dir={isRtl ? "rtl" : "ltr"}
          >
            {/* Floating mini-stat: top */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="absolute -top-5 left-6 z-20 flex items-center gap-2 px-3 py-2 rounded-full bg-white/95 dark:bg-slate-800/95 backdrop-blur border border-cyan-200/50 dark:border-cyan-500/30 shadow-lg shadow-cyan-500/10"
            >
              <Sparkles size={12} className="text-cyan-500" strokeWidth={2.5} />
              <span className="text-[11px] font-bold text-slate-900 dark:text-white">
                {t.stat1}
              </span>
              <span className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold">
                {t.stat1Label}
              </span>
            </motion.div>
            {/* Floating mini-stat: bottom */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="absolute -bottom-5 right-6 z-20 flex items-center gap-2 px-3 py-2 rounded-full bg-white/95 dark:bg-slate-800/95 backdrop-blur border border-teal-200/50 dark:border-teal-500/30 shadow-lg shadow-teal-500/10"
            >
              <Lock size={12} className="text-teal-500" strokeWidth={2.5} />
              <span className="text-[11px] font-bold text-slate-900 dark:text-white">
                {t.stat2}
              </span>
              <span className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold">
                {t.stat2Label}
              </span>
            </motion.div>

            {/* Glass card */}
            <div className="relative rounded-3xl bg-white/[0.04] backdrop-blur-xl border border-white/10 p-8 sm:p-10 shadow-2xl shadow-black/20">
              {/* Glow ring */}
              <div className="absolute inset-0 -z-10 rounded-3xl bg-gradient-to-br from-cyan-500/20 to-teal-500/10 blur-2xl" />

              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center shadow-lg shadow-cyan-500/40">
                  <Mail size={22} className="text-white" strokeWidth={2.4} />
                </div>
                <div>
                  <div className="text-xs font-bold text-cyan-300 tracking-wider uppercase">
                    {t.eyebrow}
                  </div>
                  <div className="text-base font-black text-white">
                    SpeakRoar Inside
                  </div>
                </div>
              </div>

              {status === "ok" ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center gap-3 p-5 rounded-2xl bg-green-500/15 border border-green-400/30 text-green-200"
                >
                  <div className="shrink-0 w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                    <Check size={20} strokeWidth={3.5} className="text-white" />
                  </div>
                  <span className="font-semibold text-sm leading-relaxed">
                    {t.success}
                  </span>
                </motion.div>
              ) : (
                <form onSubmit={submit}>
                  <label className="block text-xs font-bold text-slate-400 tracking-wider uppercase mb-2">
                    E-mail
                  </label>
                  <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t.placeholder}
                      dir={isRtl ? "rtl" : "ltr"}
                      className="flex-1 px-5 py-4 rounded-xl bg-white/[0.06] border border-white/15 text-white placeholder-slate-400 text-sm focus:border-cyan-400 focus:bg-white/[0.10] focus:ring-2 focus:ring-cyan-400/30 outline-none transition-all"
                    />
                    <motion.button
                      type="submit"
                      disabled={status === "loading"}
                      whileTap={{ scale: 0.96 }}
                      whileHover={{ scale: 1.02 }}
                      className="group relative px-6 py-4 rounded-xl bg-gradient-to-r from-cyan-500 via-sky-500 to-teal-500 text-white font-bold text-sm shadow-lg shadow-cyan-500/40 hover:shadow-xl hover:shadow-cyan-500/60 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 overflow-hidden whitespace-nowrap"
                    >
                      <span className="relative z-10 flex items-center gap-2">
                        {status === "loading" ? (
                          <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        ) : (
                          <>
                            {t.cta}
                            <ArrowRight
                              size={16}
                              strokeWidth={2.5}
                              className="group-hover:translate-x-1 transition-transform rtl:rotate-180"
                            />
                          </>
                        )}
                      </span>
                      {/* Hover shimmer */}
                      <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                    </motion.button>
                  </div>
                  {error && (
                    <p className="text-xs text-red-300 font-semibold mb-2">{error}</p>
                  )}
                  <p className="text-[11px] text-slate-400 font-medium leading-relaxed">
                    {t.consent}
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
